import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { FileText } from 'lucide-react';
import { AuthGuard } from './components/auth/AuthGuard';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy-loaded pages
const ProcessPage = lazy(() => import('./pages/ProcessPage').then(m => ({ default: m.ProcessPage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Templates = lazy(() => import('./pages/Templates').then(m => ({ default: m.Templates })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const CrearContrasena = lazy(() => import('./pages/CrearContrasena').then(m => ({ default: m.CrearContrasena })));
const CasesPage = lazy(() => import('./pages/CasesPage').then(m => ({ default: m.CasesPage })));
const CaseDetailPage = lazy(() => import('./pages/CaseDetailPage').then(m => ({ default: m.CaseDetailPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ImportarClientesPage = lazy(() => import('./pages/ImportarClientesPage').then(m => ({ default: m.ImportarClientesPage })));
const RecibosPage = lazy(() => import('./pages/RecibosPage').then(m => ({ default: m.RecibosPage })));
const TranscripcionesPage = lazy(() => import('./pages/TranscripcionesPage').then(m => ({ default: m.TranscripcionesPage })));
const TranscripcionRevisionPage = lazy(() => import('./pages/TranscripcionRevisionPage').then(m => ({ default: m.TranscripcionRevisionPage })));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const UIFPage = lazy(() => import('./pages/UIFPage').then(m => ({ default: m.UIFPage })));
const WhatsAppPage = lazy(() => import('./pages/WhatsAppPage').then(m => ({ default: m.WhatsAppPage })));
const PlatformPage = lazy(() => import('./pages/PlatformPage').then(m => ({ default: m.PlatformPage })));
const PresupuestosPage = lazy(() => import('./pages/PresupuestosPage').then(m => ({ default: m.PresupuestosPage })));
const CalculosPage = lazy(() => import('./pages/CalculosPage').then(m => ({ default: m.CalculosPage })));
const FoliosPage = lazy(() => import('./pages/FoliosPage').then(m => ({ default: m.FoliosPage })));
const VencimientosPage = lazy(() => import('./pages/VencimientosPage').then(m => ({ default: m.VencimientosPage })));
const DeclaranotPage = lazy(() => import('./pages/DeclaranotPage').then(m => ({ default: m.DeclaranotPage })));
const ClausulasPage = lazy(() => import('./pages/ClausulasPage').then(m => ({ default: m.ClausulasPage })));
const BlacklistPage = lazy(() => import('./pages/BlacklistPage').then(m => ({ default: m.BlacklistPage })));
const PlanesPage = lazy(() => import('./pages/PlanesPage').then(m => ({ default: m.PlanesPage })));
const AprendePage = lazy(() => import('./pages/AprendePage').then(m => ({ default: m.AprendePage })));

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center justify-center h-screen gap-4">
      <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center animate-pulse">
        <FileText className="h-6 w-6 text-primary-foreground" />
      </div>
      <p className="text-sm text-muted-foreground animate-fade-in">Cargando...</p>
    </div>
  );
}

function App() {
  // Rearma el ErrorBoundary en cada cambio de ruta: sin esta llave, una
  // pantalla que se cayo deja el error puesto y ninguna otra vuelve a
  // dibujarse aunque el usuario navegue a otro lado.
  const location = useLocation();

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(0 0% 100%)',
            color: 'hsl(222.2 84% 4.9%)',
            border: '1px solid hsl(214.3 31.8% 91.4%)',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
          success: {
            iconTheme: {
              primary: 'hsl(142 71% 45%)',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: 'hsl(0 84.2% 60.2%)',
              secondary: '#fff',
            },
          },
        }}
      />
      <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<Login />} />
          {/* Publicas: donde aterrizan las ligas de invitacion y de recuperacion.
              /reset-password es a donde manda "olvide mi contrasena" desde el
              login; antes no existia y caia en el comodin de abajo. */}
          <Route path="/crear-contrasena" element={<CrearContrasena />} />
          <Route path="/reset-password" element={<CrearContrasena />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/generate"
            element={
              <AuthGuard>
                <ProcessPage />
              </AuthGuard>
            }
          />
          <Route
            path="/templates"
            element={
              <AuthGuard>
                <Templates />
              </AuthGuard>
            }
          />
          <Route
            path="/history"
            element={
              <AuthGuard>
                <History />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
          <Route
            path="/cases"
            element={
              <AuthGuard>
                <CasesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cases/:caseId"
            element={
              <AuthGuard>
                <CaseDetailPage />
              </AuthGuard>
            }
          />
          <Route
            path="/calendario"
            element={
              <AuthGuard>
                <CalendarPage />
              </AuthGuard>
            }
          />
          <Route
            path="/reportes"
            element={
              <AuthGuard>
                <ReportsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/uif"
            element={
              <AuthGuard>
                <UIFPage />
              </AuthGuard>
            }
          />
          <Route
            path="/whatsapp"
            element={
              <AuthGuard>
                <WhatsAppPage />
              </AuthGuard>
            }
          />

          <Route
            path="/platform"
            element={
              <AuthGuard>
                <PlatformPage />
              </AuthGuard>
            }
          />

          <Route path="/presupuestos" element={<AuthGuard><PresupuestosPage /></AuthGuard>} />
          <Route path="/calculos" element={<AuthGuard><CalculosPage /></AuthGuard>} />
          <Route path="/folios" element={<AuthGuard><FoliosPage /></AuthGuard>} />
          <Route path="/vencimientos" element={<AuthGuard><VencimientosPage /></AuthGuard>} />
          <Route path="/declaranot" element={<AuthGuard><DeclaranotPage /></AuthGuard>} />
          <Route path="/clausulas" element={<AuthGuard><ClausulasPage /></AuthGuard>} />
          <Route path="/clientes" element={<AuthGuard><ClientsPage /></AuthGuard>} />
          {/* Antes que /clientes/:clientId: si no, "importar" se tomaria por un id. */}
          <Route path="/clientes/importar" element={<AuthGuard><ImportarClientesPage /></AuthGuard>} />
          <Route path="/clientes/:clientId" element={<AuthGuard><ClientDetailPage /></AuthGuard>} />
          <Route path="/recibos" element={<AuthGuard><RecibosPage /></AuthGuard>} />
          <Route path="/transcripciones" element={<AuthGuard><TranscripcionesPage /></AuthGuard>} />
          <Route path="/transcripciones/:id" element={<AuthGuard><TranscripcionRevisionPage /></AuthGuard>} />
          <Route path="/blacklist" element={<AuthGuard><BlacklistPage /></AuthGuard>} />
          <Route path="/planes" element={<AuthGuard><PlanesPage /></AuthGuard>} />
          <Route path="/aprende" element={<AuthGuard><AprendePage /></AuthGuard>} />

          {/* Fallback route - redirect to login */}
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default App;
